import { useState, useEffect, useCallback, useRef } from 'react';
import { useEventSource } from './hooks/useEventSource';
import NavSidebar from './components/NavSidebar';
import Modal from './components/Modal';
import EbookWizard from './components/EbookWizard';
import CommandPalette from './components/CommandPalette';
import Toasts from './components/Toasts';
import Library from './views/Library';
import EbooksView from './views/EbooksView';
import PieceDetail from './views/PieceDetail';
import VolumeDetail from './views/VolumeDetail';
import QualityDashboard from './views/QualityDashboard';
import ChannelsView from './views/ChannelsView';
import SettingsView from './views/SettingsView';
import ActivityView from './views/ActivityView';
import LinkedInView from './views/LinkedInView';
import OutreachView from './views/OutreachView';
import CostView from './views/CostView';
import NewsletterView from './views/NewsletterView';
import SocialStudioView from './views/SocialStudioView';
import CommentsApprovalView from './views/CommentsApprovalView';
import AttentionView from './views/AttentionView';
import ChannelPreviewModal, { ChannelPreviewData } from './components/ChannelPreviewModal';
import BrandPerformanceTab from './components/BrandPerformanceTab';
import LoginView from './views/LoginView';
import { useAuth } from './hooks/useAuth';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'running';
  title: string;
  detail?: string;
}

interface PieceData {
  slug: string;
  title?: string;
  lifecycleState: string;
  updatedAt?: string;
  stages: Record<string, any>;
  renditions: Record<string, any>;
  publications: Record<string, any>;
  pipelineStatus?: string;
  runningStage?: string;
  failedStage?: string;
  completedStages?: string[];
}

interface VolumeData {
  slug: string;
  title: string;
  lifecycleState: string;
  chapterList: { slug: string; title?: string; order: number }[];
}

// ── Helpers ─────────────────────────────────────────────────────────────

async function safeFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
  }
  return res;
}

export default function App() {
  // Auth (Google SSO). When SSO is disabled server-side, state is 'open' and
  // the dashboard renders normally. When enabled, an unauthenticated user gets
  // the login screen; the session cookie then rides every fetch + SSE.
  const { state: auth, refresh: refreshAuth, logout: authLogout } = useAuth();

  // Navigation
  const [view, setView] = useState<string>('library');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<'piece' | 'volume'>('piece');
  const [engineMode, setEngineMode] = useState(false);

  // Brand
  const [brand, setBrand] = useState<string>('');
  const [brands, setBrands] = useState<string[]>([]);
  const [attentionManaged, setAttentionManaged] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  // Theme toggle — text-labelled (no emoji glyphs that don't render in
  // Space Grotesk). Hydrates from <html data-theme> which is set by
  // index.html before React mounts. Persists to localStorage.
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  });
  const switchTheme = (next: 'light' | 'dark') => {
    setTheme(next);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', next);
      try { localStorage.setItem('ce-theme', next); } catch { /* private mode */ }
    }
  };
  const brandRef = useRef<HTMLDivElement>(null);

  // Data
  const [pieces, setPieces] = useState<PieceData[]>([]);
  const [volumes, setVolumes] = useState<VolumeData[]>([]);

  // UI
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState<{ channel: 'dev-to' | 'hashnode'; slug: string; data: ChannelPreviewData } | null>(null);
  const [previewSaving, setPreviewSaving] = useState(false);
  const toastIdRef = useRef(0);
  // Track running-pipeline toast IDs by slug so COMPLETED/FAILED can dismiss them
  const pipelineToastRef = useRef<Map<string, string>>(new Map());

  // Helper: dismiss the running pipeline toast for a slug (component-level so all handlers can use it)
  const dismissPipelineToast = useCallback((s: string) => {
    const tid = pipelineToastRef.current.get(s);
    if (tid) {
      setToasts((p) => p.filter((t) => t.id !== tid));
      pipelineToastRef.current.delete(s);
    }
  }, []);

  const addToast = useCallback((type: Toast['type'], title: string, detail?: string) => {
    const id = `t-${++toastIdRef.current}`;
    setToasts((p) => [...p, { id, type, title, detail }]);
    if (type !== 'running') {
      setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 5000);
    }
    return id;
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((p) => p.filter((t) => t.id !== id));
  }, []);

  const replaceToast = useCallback((id: string, type: Toast['type'], title: string, detail?: string) => {
    setToasts((p) => p.map((t) => t.id === id ? { ...t, type, title, detail } : t));
    if (type !== 'running') {
      setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 5000);
    }
  }, []);

  // ── Data fetching ──────────────────────────────────────────────────

  useEffect(() => {
    // Wait until auth is resolved before hitting gated APIs. On a fresh SSO
    // login the component mounts while still unauthenticated ('loading' →
    // 'anon'); fetching here would 401 and, with [] deps, never retry — leaving
    // the brand unset and the Library empty. Re-running on auth.status flip
    // (→ 'authed') fixes that. In open mode this fires once auth resolves to
    // 'open'. The cookie rides the request automatically (same-origin).
    if (auth.status !== 'authed' && auth.status !== 'open') return;
    safeFetch('/api/brands').then((r) => r.json())
      .then((d: { brands: string[]; defaultBrand?: string }) => {
        setBrands(d.brands);
        if (d.brands.length > 0 && !brand) {
          const preferred = d.defaultBrand && d.brands.includes(d.defaultBrand) ? d.defaultBrand : d.brands[0];
          setBrand(preferred);
        }
      })
      .catch(() => addToast('error', 'Failed to load brands'));
  }, [auth.status]);

  useEffect(() => {
    if (!brand) return;
    setSelectedSlug(null);
    setView('library');
    // Clear the previous brand's pieces/volumes IMMEDIATELY, before the async
    // refetch below. Otherwise the Library/Ebooks list keeps showing the old
    // brand's items during the in-flight fetch — the user can open one (e.g. a
    // zopnight ebook) while `brand` has already flipped to zopdev, and every
    // action then targets /api/zopdev/... → a confusing "volume not found".
    setPieces([]);
    setVolumes([]);
    setAttentionManaged(false);
    // #181: clear cached topic suggestions on brand switch — otherwise the
    // "only fetch when empty" guard would show the previous brand's suggestions.
    setTopicSuggestions([]);
    setTopicEvaluated(null);
    setTopicMessage('');
    Promise.all([
      // includeScratch=1 so Social Studio drafts (scratch-- pieces) are loaded;
      // the Library keeps them out of the default view and surfaces them under
      // its "Social drafts" filter so they're recoverable, not lost.
      safeFetch(`/api/${brand}/pieces?includeScratch=1`).then((r) => r.json()),
      safeFetch(`/api/${brand}/volumes`).then((r) => r.json()),
      // Capability is additive. A rolling deploy with an older server must not
      // blank the whole Library just because this optional endpoint is absent.
      safeFetch(`/api/${brand}/attention/capability`).then((r) => r.json()).catch(() => ({ enabled: false })),
    ]).then(([rp, rv, attentionCapability]: [any[], any[], { enabled?: boolean }]) => {
      setPieces(rp.map((x: any) => ({ ...x, lifecycleState: x.lifecycleState || x.state || 'spark', publications: x.publications ?? {} })));
      setVolumes(rv.map((x: any) => ({
        ...x, lifecycleState: x.lifecycleState || x.state || 'spark',
        chapterList: (x.chapterList || []).map((c: any) => ({ slug: c.slug, title: c.title || c.slug, order: c.order ?? 0 })),
      })));
      setAttentionManaged(attentionCapability.enabled === true);
    }).catch(() => addToast('error', 'Failed to load data'));
  }, [brand]);

  // A stale bookmark or browser history entry must not reopen either legacy
  // engagement workflow after the brand capability has loaded.
  useEffect(() => {
    if (attentionManaged && (view === 'linkedin' || view === 'comments-approval')) {
      setSelectedSlug(null);
      setView('attention');
    }
  }, [attentionManaged, view]);

  // ── SSE via useEventSource hook ────────────────────────────────────
  // Single source of truth for real-time updates. No polling fallback.

  const brandRef2 = useRef(brand);
  brandRef2.current = brand;

  const handleSSEEvent = useCallback((ev: Record<string, unknown>) => {
    const type = ev.type as string;
    const slug = ev.slug as string;
    const stage = ev.stage as string | undefined;

    const update = (s: string, fn: (p: PieceData) => PieceData) => {
      setPieces((ps) => ps.map((p) => p.slug === s ? fn(p) : p));
    };

    switch (type) {
      case 'PIPELINE_STARTED': {
        // Pipeline runs in Claude Code, not autonomously in the engine.
        // The SSE event fires when the queue task is picked up; the actual
        // brief / plan / compose stages happen when Claude Code processes it
        // in chat. Show the status honestly.
        update(slug, (p) => ({ ...p, pipelineStatus: 'running', completedStages: [], failedStage: undefined, runningStage: undefined }));
        dismissPipelineToast(slug);
        const tid = addToast('running', `Generating via API: ${slug}…`);
        pipelineToastRef.current.set(slug, tid);
        break;
      }
      case 'STAGE_STARTED':
        update(slug, (p) => ({ ...p, runningStage: stage }));
        break;
      case 'STAGE_COMPLETED':
        update(slug, (p) => ({ ...p, runningStage: undefined, completedStages: [...(p.completedStages || []), stage!] }));
        break;
      case 'STAGE_FAILED':
        update(slug, (p) => ({ ...p, runningStage: undefined, failedStage: stage, pipelineStatus: 'failed' }));
        addToast('error', `Failed: ${stage}`, ev.error as string);
        break;
      case 'STAGE_SKIPPED':
        update(slug, (p) => ({ ...p, runningStage: undefined, completedStages: [...(p.completedStages || []), stage!] }));
        break;
      case 'PIPELINE_COMPLETED':
        update(slug, (p) => ({ ...p, pipelineStatus: 'completed', runningStage: undefined }));
        dismissPipelineToast(slug);
        addToast('success', `Content ready: ${slug}`);
        // Re-fetch full piece detail to get final stages/renditions/publications
        safeFetch(`/api/${brandRef2.current}/pieces/${slug}`).then((r) => r.json())
          .then((d) => update(slug, (p) => ({
            ...p,
            lifecycleState: d.lifecycleState || d.state || p.lifecycleState,
            stages: d.stages ?? p.stages,
            renditions: d.renditions ?? p.renditions,
            publications: d.publications ?? p.publications,
            title: d.title ?? p.title,
            updatedAt: d.updatedAt ?? p.updatedAt,
          })))
          .catch(() => {});
        // Also refresh volumes in case a volume pipeline completed (updates chapterList)
        safeFetch(`/api/${brandRef2.current}/volumes`).then((r) => r.json())
          .then((rv: any[]) => setVolumes(rv.map((x: any) => ({
            ...x, lifecycleState: x.lifecycleState || x.state || 'spark',
            chapterList: (x.chapterList || []).map((c: any) => ({ slug: c.slug, title: c.title || c.slug, order: c.order ?? 0 })),
          }))))
          .catch(() => {});
        break;
      case 'PIPELINE_FAILED':
        update(slug, (p) => ({ ...p, pipelineStatus: 'failed', runningStage: undefined }));
        dismissPipelineToast(slug);
        addToast('error', `Pipeline failed: ${slug}`, ev.error as string | undefined);
        break;
      case 'STATE_TRANSITION':
        update(slug, (p) => ({ ...p, lifecycleState: ev.to as string }));
        break;
      case 'RENDITION_COMPLETED':
        addToast('success', `Format ready: ${ev.renditionType as string}`);
        dismissPipelineToast(slug);
        // Re-fetch piece to update renditions + always clear per-format run state
        safeFetch(`/api/${brandRef2.current}/pieces/${slug}`).then(r => r.json())
          .then(d => update(slug, p => ({
            ...p,
            renditions: d.renditions ?? p.renditions,
            pipelineStatus: undefined,
            runningStage: undefined,
          })))
          .catch(() => {});
        break;
      case 'VALIDATION_SCORE':
        addToast(ev.passed ? 'success' : 'error', `Quality: ${ev.score}/100`, ev.renditionType as string);
        break;
    }
  }, [addToast, dismissToast, dismissPipelineToast]);

  const { status: sseStatus } = useEventSource({
    url: brand ? `/api/${brand}/canvas-events` : null,
    onEvent: handleSSEEvent,
    onError: () => addToast('error', 'Live connection lost, reconnecting...'),
  });

  // ── Actions ────────────────────────────────────────────────────────

  const handleAction = useCallback((action: string, slug: string) => {
    if (action === 'social-studio') {
      // Hand off to Social Studio with the piece pre-selected — skips the
      // empty-state guard so the user lands directly on the platform-pick
      // step for THIS piece's narrative.md.
      setSelectedSlug(slug);
      setChapterContext(null);
      setView('social-studio');
      return;
    }
    if (action === 'pipeline') {
      // Prevent duplicate triggers if already running
      const current = pieces.find((p) => p.slug === slug);
      if (current?.pipelineStatus === 'running') return;
      const isVol = volumes.some((v) => v.slug === slug);
      // Optimistic running state
      setPieces((ps) => ps.map((p) => p.slug === slug
        ? { ...p, pipelineStatus: 'running', completedStages: [], failedStage: undefined, runningStage: undefined }
        : p));
      // Use a raw fetch (not safeFetch) so we can read the JSON body even on a
      // non-2xx response — a 404 "volume not found" carries a useful message we
      // want to show verbatim, not swallow into a generic "Failed to start".
      fetch(`/api/${brand}/${isVol ? 'volumes' : 'pieces'}/${slug}/pipeline`, { method: 'POST' })
        .then((r) => r.json())
        .then((d: { mode?: string; started?: boolean; error?: string }) => {
          if (d.mode === 'llm') {
            addToast('running', isVol
              ? `Generating via API: ${slug} — all chapters composing in one pass...`
              : `Generating via API: ${slug} — research → plan → compose running...`);
            return;
          }
          // Not started — clear the optimistic running state and show why.
          setPieces((ps) => ps.map((p) => p.slug === slug ? { ...p, pipelineStatus: undefined } : p));
          if (d.mode === 'not-found') {
            addToast('error', `Can't generate ${slug}`, d.error || 'Volume not found — reload and re-create it.');
          } else {
            addToast('error', `API not available for ${slug}.`, d.error || 'Check OPENROUTER_API_KEY in .env.');
          }
        })
        .catch((err) => {
          setPieces((ps) => ps.map((p) => p.slug === slug ? { ...p, pipelineStatus: undefined } : p));
          addToast('error', `Failed to start: ${slug}`, err.message);
        });
      // No polling fallback — SSE handles all progress updates
    } else if (action === 'heal') {
      const toastId = addToast('running', `Running auto-fix on ${slug}...`);
      safeFetch(`/api/${brand}/pieces/${slug}/auto-fix`, { method: 'POST' })
        .then((r) => r.json())
        .then((d) => {
          if (d.ok) {
            const msg = d.fixCount > 0
              ? `${d.fixCount} fix(es) applied — Grade ${d.overallGrade}`
              : `No auto-fixable issues — Grade ${d.overallGrade}`;
            replaceToast(toastId, 'success', msg);
            // Update evalScore in piece list so score ring refreshes
            setPieces((ps) => ps.map((p) => p.slug === slug
              ? { ...p, evalScore: d.scores ? Math.round((d.scores.seo.score + d.scores.geo.score + d.scores.virality.score) / 3) : p.evalScore }
              : p));
          } else {
            replaceToast(toastId, 'error', `Auto-fix failed: ${d.error || 'unknown error'}`);
          }
        })
        .catch((err) => replaceToast(toastId, 'error', `Auto-fix failed: ${err.message}`));
    } else if (action.startsWith('advance:')) {
      const target = action.split(':')[1];
      safeFetch(`/api/${brand}/pieces/${slug}/advance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetState: target }),
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.ok) {
            addToast('success', `Moved to ${target}`);
            setPieces((ps) => ps.map((p) => p.slug === slug ? { ...p, lifecycleState: target } : p));
          }
        })
        .catch(() => addToast('error', 'Failed'));
    } else if (action.startsWith('render:')) {
      const format = action.split(':')[1];
      setPieces((ps) => ps.map((p) => p.slug === slug
        ? { ...p, pipelineStatus: 'running', runningStage: `render:${format}` }
        : p));
      safeFetch(`/api/${brand}/pieces/${slug}/render/${format}`, { method: 'POST' })
        .then(() => {
          dismissPipelineToast(slug);
          const tid = addToast('running', `Generating ${format}: ${slug}`);
          pipelineToastRef.current.set(slug, tid);
        })
        .catch((err) => {
          setPieces((ps) => ps.map((p) => p.slug === slug
            ? { ...p, pipelineStatus: undefined, runningStage: undefined }
            : p));
          addToast('error', `Failed to generate ${format}`, err.message);
        });
    } else if (action.startsWith('queue:')) {
      const kind = action.split(':')[1];
      if (kind === 'recompose') {
        // Run compose via OpenRouter API directly — no Claude Code queue needed
        const toastId = addToast('running', `Recomposing ${slug} via API...`);
        safeFetch(`/api/${brand}/pieces/${slug}/pipeline`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stages: ['compose'] }),
        })
          .then((r) => r.json())
          .then((d: { mode?: string }) => {
            if (d.mode === 'llm') {
              replaceToast(toastId, 'running', `Recomposing ${slug} — compose → enrich → eval running via API. Score will update when done.`);
            } else {
              replaceToast(toastId, 'error', `API not available. Check OPENROUTER_API_KEY in .env.`);
            }
          })
          .catch((err) => replaceToast(toastId, 'error', `Recompose failed: ${err.message}`));
      } else {
        const toastId = addToast('running', `Queueing ${kind} for ${slug}...`);
        safeFetch(`/api/${brand}/pieces/${slug}/queue/${kind}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
          .then((r) => r.json())
          .then((d) => {
            if (d.queued) replaceToast(toastId, 'success', `${kind} queued (${d.taskId.slice(-6)}).`);
            else replaceToast(toastId, 'error', d.error || 'Queue failed');
          })
          .catch((err) => replaceToast(toastId, 'error', `Queue failed: ${err.message}`));
      }
    } else if (action === 'eval-all') {
      const toastId = addToast('running', `Re-evaluating ${slug}...`);
      safeFetch(`/api/${brand}/pieces/${slug}/eval-all`, { method: 'POST' })
        .then((r) => r.json())
        .then((d) => {
          if (d.ok) {
            const sc = d.scores;
            replaceToast(toastId, 'success', `Eval done — ${sc.overall}/100 (${sc.grade}). Top fix: ${(d.topFixes && d.topFixes[0]) || 'none'}`);
            setPieces((ps) => ps.map((p) => p.slug === slug ? { ...p, evalScore: sc.overall } : p));
          } else replaceToast(toastId, 'error', d.error || 'Eval failed');
        })
        .catch((err) => replaceToast(toastId, 'error', `Eval failed: ${err.message}`));
    } else if (action.startsWith('preview:')) {
      const rawCh = action.split(':')[1];
      const channel = (rawCh === 'devto' ? 'dev-to' : rawCh) as 'dev-to' | 'hashnode';
      const toastId = addToast('running', `Preparing ${channel} preview for ${slug}...`);
      safeFetch(`/api/${brand}/pieces/${slug}/preview-channel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel }),
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.ok && d.preview) {
            replaceToast(toastId, 'success', `Preview ready for ${channel}`);
            setPreviewModal({ channel, slug, data: d.preview as ChannelPreviewData });
          } else {
            replaceToast(toastId, 'error', d.error || 'Preview failed');
          }
        })
        .catch((err) => replaceToast(toastId, 'error', `Preview failed: ${channel}`, err.message));
    } else if (action.startsWith('publish:')) {
      // Action format: `publish:${channel}` (legacy) OR
      // `publish:${channel}:${format}` (new — from the per-channel type
      // dropdown in AppleView's PublishSection). The optional segment after
      // the channel tells the server which format to publish when multiple
      // formats are routed to the same channel (e.g. X → x-single /
      // x-thread / x-hottake / x-listicle-thread).
      //
      // Defensive split: rejoin everything after the second `:` so a future
      // format id containing a colon (unlikely but not contractually
      // impossible — formatIds are kebab-case today) doesn't silently
      // truncate.
      const parts = action.split(':');
      const channel = parts[1];
      const format = parts.length > 2 ? parts.slice(2).join(':') : undefined;
      const toastId = addToast('running', `Publishing ${slug} to ${channel}${format ? ` (${format})` : ''}...`);
      const body: { channel: string; format?: string } = { channel };
      if (format) body.format = format;
      safeFetch(`/api/${brand}/pieces/${slug}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((d) => {
          replaceToast(toastId, 'success', `Published to ${channel}`, d.permalink);
          setPieces((ps) => ps.map((p) => p.slug === slug
            ? { ...p, publications: { ...p.publications, [channel]: { permalink: d.permalink, publishedAt: new Date().toISOString() } } }
            : p));
        })
        .catch((err) => replaceToast(toastId, 'error', `Publish failed: ${channel}`, err.message));
    }
  }, [brand, volumes, addToast, replaceToast, dismissPipelineToast]);

  // ── Keyboard ───────────────────────────────────────────────────────

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen((p) => !p); }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'e') { e.preventDefault(); setEngineMode((p) => !p); }
      if (e.key === 'Escape') { if (cmdOpen) setCmdOpen(false); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [cmdOpen]);

  // Brand dropdown outside click
  useEffect(() => {
    if (!brandOpen) return;
    const h = (e: MouseEvent) => { if (brandRef.current && !brandRef.current.contains(e.target as Node)) setBrandOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [brandOpen]);

  // ── Navigation ─────────────────────────────────────────────────────

  // Chapter context for viewing chapters inside volumes
  const [chapterContext, setChapterContext] = useState<{ volumeSlug: string; chapterSlug: string } | null>(null);

  const navigate = (v: string) => { setView(v); setSelectedSlug(null); setChapterContext(null); };
  const openPiece = (slug: string) => { setSelectedSlug(slug); setSelectedType('piece'); setChapterContext(null); setView('piece-detail'); };
  const openVolume = (slug: string) => { setSelectedSlug(slug); setSelectedType('volume'); setChapterContext(null); setView('volume-detail'); };
  const openInSocialStudio = (slug: string) => {
    setSelectedSlug(slug);
    setChapterContext(null);
    setView('social-studio');
  };
  const openChapter = (volumeSlug: string, chapterSlug: string) => {
    setChapterContext({ volumeSlug, chapterSlug });
    setSelectedSlug(chapterSlug);
    setView('chapter-detail');
  };

  // ── Topic suggestions ───────────────────────────────────────────────

  type Suggestion = {
    title: string;
    cluster: string;
    rationale: string;
    proposedSlug: string;
    kind: string;
    score: number;
    /** LLM's one-sentence justification for the score (PR-2 #82). Optional for legacy yaml. */
    scoreReason?: string;
    /** Time-to-narrative estimate (PR-2 #82). Optional for legacy yaml. */
    effortHours?: number;
  };

  type AxisResult = { score: number; applies: boolean; reason: string };
  type EvalReport = { composite: number; axes: Record<string, AxisResult> };
  type EvaluatedItem = { suggestion: Suggestion; report: EvalReport };
  type GapsData = {
    totalPosts: number;
    clusterCoverage: Record<string, number>;
    underCoveredClusters: string[];
  };

  const [topicsOpen, setTopicsOpen] = useState(false);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicSuggestions, setTopicSuggestions] = useState<Suggestion[]>([]);
  const [topicEvaluated, setTopicEvaluated] = useState<EvaluatedItem[] | null>(null);
  const [topicMessage, setTopicMessage] = useState<string>('');

  const loadTopicSuggestions = () => {
    setTopicsLoading(true);
    setTopicMessage('');
    safeFetch(`/api/${brand}/topics/suggestions`)
      .then((r) => r.json())
      .then((data: { gaps: GapsData; suggestions: Suggestion[]; evaluated?: EvaluatedItem[] }) => {
        setTopicSuggestions(data.suggestions || []);
        setTopicEvaluated(data.evaluated || null);
      })
      .catch((err) => setTopicMessage(`Failed to load: ${err.message}`))
      .finally(() => setTopicsLoading(false));
  };

  const refreshTopicPrompt = (forceFresh: boolean) => {
    setTopicsLoading(true);
    setTopicMessage(forceFresh ? 'Archiving current batch and generating fresh prompt...' : 'Generating prompt...');
    safeFetch(`/api/${brand}/topics/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ forceFresh }),
    })
      .then((r) => r.json())
      .then((data: { message: string; gaps: GapsData; archivedCount?: number; suggestions?: Suggestion[]; evaluated?: EvaluatedItem[] }) => {
        setTopicMessage(data.message);
        if (data.suggestions && data.suggestions.length > 0) {
          // OpenRouter API mode — suggestions returned directly in response.
          // The refresh path now returns fresh `evaluated` scores keyed to these
          // new titles (server, Riya #8). Always overwrite: if evaluated is
          // absent, clear the old array so stale scores keyed to prior titles
          // never render next to new suggestions.
          setTopicSuggestions(data.suggestions);
          setTopicEvaluated(data.evaluated ?? null);
          addToast('success', `Generated ${data.suggestions.length} topic suggestions via API.`);
        } else {
          if (forceFresh) {
            setTopicSuggestions([]);
            setTopicEvaluated(null);
          }
          addToast('running', forceFresh
            ? `Fresh batch requested. ${data.archivedCount ?? 0} suggestions archived.`
            : `Generating suggestions via API — this may take a moment.`);
        }
      })
      .catch((err) => setTopicMessage(`Failed: ${err.message}`))
      .finally(() => setTopicsLoading(false));
  };

  const dismissSuggestion = (title: string) => {
    safeFetch(`/api/${brand}/topics/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    })
      .then(() => {
        setTopicSuggestions((current) => current.filter((s) => s.title !== title));
        setTopicEvaluated((current) =>
          current ? current.filter((e) => e.suggestion.title !== title) : null,
        );
        addToast('success', `Dismissed: ${title.slice(0, 40)}…`);
      })
      .catch((err) => addToast('error', `Dismiss failed`, err.message));
  };

  const useTopicSuggestion = (s: Suggestion) => {
    setNewPieceTopic(s.title);
    setTopicsOpen(false);
    setNewPieceOpen(true);
  };

  // ── New piece ──────────────────────────────────────────────────────

  const [newPieceOpen, setNewPieceOpen] = useState(false);
  const [newPieceTopic, setNewPieceTopic] = useState('');

  const handleNewPiece = () => {
    if (!newPieceTopic.trim()) return;
    const slug = newPieceTopic.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const topic = newPieceTopic.trim();
    const toastId = addToast('running', `Creating piece: ${slug}`);
    setNewPieceOpen(false);
    setNewPieceTopic('');

    safeFetch(`/api/${brand}/pieces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, topic, type: 'idea' }),
    })
      .then((r) => r.json())
      .then(() => {
        // Pieces are created in 'spark' state. The actual research/plan/compose
        // stages run in Claude Code (chat), not autonomously in the engine —
        // see CLAUDE.md. The dashboard enqueues; Claude Code processes.
        const newPiece: PieceData = {
          slug, title: topic, lifecycleState: 'spark',
          stages: {}, renditions: {}, publications: {},
          pipelineStatus: 'idle', completedStages: [],
        };
        setPieces((ps) => [newPiece, ...ps]);
        replaceToast(toastId, 'success', `Created: ${slug}`);
        openPiece(slug);
        // Trigger pipeline — runs via OpenRouter API if configured, else queues for Claude Code
        return safeFetch(`/api/${brand}/pieces/${slug}/pipeline`, { method: 'POST' }).then((r) => r.json());
      })
      .then((d: { mode?: string }) => addToast(
        'running',
        d?.mode === 'llm'
          ? `Generating via API: ${slug} — research → plan → compose running...`
          : `API not available. Check OPENROUTER_API_KEY in .env.`,
      ))
      .catch((err) => replaceToast(toastId, 'error', `Failed to create piece`, err.message));
  };

  // ── New volume ──────────────────────────────────────────────────────

  const [newVolumeOpen, setNewVolumeOpen] = useState(false);

  // The wizard now owns create → plan → chapter-edit → generate; this just
  // refreshes the volume list and opens the newly-created/generating volume.
  const handleVolumeCreated = (slug: string, volBrand: string) => {
    setNewVolumeOpen(false);
    replaceToast(addToast('running', `Generating ebook: ${slug}`), 'success', `Created: ${slug}`);
    openVolume(slug);
    safeFetch(`/api/${volBrand}/volumes`).then((r) => r.json()).then((rv: any[]) =>
      setVolumes(rv.map((x: any) => ({
        ...x, lifecycleState: x.lifecycleState || x.state || 'spark',
        chapterList: (x.chapterList || []).map((c: any) => ({ slug: c.slug, title: c.title || c.slug, order: c.order ?? 0 })),
      })))
    ).catch(() => {});
  };

  // ── Derived state ──────────────────────────────────────────────────

  const selectedPiece = pieces.find((p) => p.slug === selectedSlug) || null;
  const selectedVolume = volumes.find((v) => v.slug === selectedSlug) || null;

  let viewTitle = 'Library';
  if (view === 'piece-detail' && selectedPiece) viewTitle = `Library / ${selectedPiece.title || selectedPiece.slug}`;
  else if (view === 'volume-detail' && selectedVolume) viewTitle = `Ebooks / ${selectedVolume.title || selectedVolume.slug}`;
  else if (view === 'chapter-detail' && chapterContext) {
    const vol = volumes.find(v => v.slug === chapterContext.volumeSlug);
    const volTitle = vol?.title || chapterContext.volumeSlug;
    const chTitle = chapterContext.chapterSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    viewTitle = `Ebooks / ${volTitle} / ${chTitle}`;
  }
  else if (view === 'ebooks') viewTitle = 'Ebooks';
  else if (view === 'quality') viewTitle = 'Quality';
  else if (view === 'brand-performance') viewTitle = 'Brand Performance';
  else if (view === 'cost') viewTitle = 'Spend';
  else if (view === 'social-studio') viewTitle = 'Social Studio';
  else if (view === 'attention') viewTitle = 'Attention';
  else if (view === 'linkedin') viewTitle = 'LinkedIn Comments';
  else if (view === 'newsletter') viewTitle = 'Newsletter';
  else if (view === 'channels') viewTitle = 'Channels';
  else if (view === 'settings') viewTitle = 'Settings';
  else if (view === 'activity') viewTitle = 'Activity';
  else if (view === 'comments-approval') viewTitle = 'Comments approval';

  // ── Breadcrumb ancestor (clickable parent when a detail view is open) ──
  // Parent mapping mirrors NavSidebar: piece-detail -> library, volume/chapter -> ebooks.
  let breadcrumbParent: { view: string; label: string } | null = null;
  if (view === 'piece-detail') breadcrumbParent = { view: 'library', label: 'Library' };
  else if (view === 'volume-detail' || view === 'chapter-detail') breadcrumbParent = { view: 'ebooks', label: 'Ebooks' };
  // Leaf title: drop the compound "Parent / " prefix so the ancestor isn't shown twice.
  const breadcrumbLeaf = breadcrumbParent
    // lastIndexOf so a chapter (Ebooks / vol / chapter) shows just the chapter as the leaf
    ? viewTitle.slice(viewTitle.lastIndexOf('/') + 1).trim()
    : viewTitle;

  // ── SSE connection indicator ───────────────────────────────────────

  const sseIndicator = sseStatus === 'open' ? '\u25CF' : sseStatus === 'connecting' ? '\u25CB' : '\u25CF';
  // SSE indicator colours map to brand palette — no red in the system.
  const sseColor = sseStatus === 'open'
    ? 'var(--zop-green, #7FB236)'
    : sseStatus === 'connecting'
    ? 'var(--zop-orange, #F58549)'
    : 'var(--zop-orange, #F58549)';

  // ── Auth gate (client-side; the server gate is the real enforcement) ──
  if (auth.status === 'loading') {
    return (
      <div className="login">
        <div className="login__card">
          <div className="login__brand">Content Engine</div>
          <p className="login__hint">Loading…</p>
        </div>
      </div>
    );
  }
  if (auth.status === 'error') {
    return (
      <div className="login">
        <div className="login__card">
          <div className="login__brand">Content Engine</div>
          <p className="login__sub">Could not reach the server.</p>
          <button type="button" className="topbar__logout" onClick={refreshAuth}>Retry</button>
        </div>
      </div>
    );
  }
  if (auth.status === 'anon') {
    return <LoginView clientId={auth.clientId} domain={auth.domain} onLogin={refreshAuth} />;
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar__logo">
          <span className="topbar__logo-mark">CE</span>
          Content Engine
        </div>
        <div className="topbar__spacer" />
        {breadcrumbParent ? (
          <span className="topbar__breadcrumb">
            <button
              type="button"
              className="topbar__breadcrumb-link"
              onClick={() => navigate(breadcrumbParent!.view)}
            >{breadcrumbParent.label}</button>
            <span aria-hidden="true"> {'›'} </span>
            {breadcrumbLeaf}
          </span>
        ) : (
          <span className="topbar__breadcrumb">{viewTitle}</span>
        )}
        <div className="topbar__spacer" />
        <span style={{ fontSize: 10, color: sseColor, marginRight: 8 }} title={`SSE: ${sseStatus}`}>{sseIndicator} live</span>
        <div className="segmented" role="group" aria-label="Theme">
          <button
            type="button"
            className={`seg ${theme === 'light' ? 'active' : ''}`}
            onClick={() => switchTheme('light')}
            aria-pressed={theme === 'light'}
          >Light</button>
          <button
            type="button"
            className={`seg ${theme === 'dark' ? 'active' : ''}`}
            onClick={() => switchTheme('dark')}
            aria-pressed={theme === 'dark'}
          >Dark</button>
        </div>
        <button className="topbar__search-btn" onClick={() => setCmdOpen(true)}>
          <span style={{ flex: 1, textAlign: 'left' }}>Search</span>
          <kbd>{'\u2318'}K</kbd>
        </button>
        <div ref={brandRef} style={{ position: 'relative' }}>
          <button className="topbar__brand-btn" onClick={() => setBrandOpen(!brandOpen)} aria-expanded={brandOpen} aria-haspopup="listbox">
            <span className="topbar__brand-dot" />
            <span>{brand}</span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{
                marginLeft: 4,
                color: 'var(--g-500)',
                transform: brandOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform var(--dur-fast) var(--ease-out)',
              }}
            >
              <path d="M4 6l4 4 4-4" />
            </svg>
          </button>
          {brandOpen && brands.length > 1 && (
            <div className="topbar__brand-dropdown">
              {brands.map((b) => (
                <button key={b} className={`topbar__brand-option ${b === brand ? 'topbar__brand-option--active' : ''}`}
                  onClick={() => { setBrandOpen(false); if (b !== brand) setBrand(b); }}>{b}</button>
              ))}
            </div>
          )}
        </div>
        {auth.status === 'authed' && (
          <div className="topbar__user" title={auth.user.email}>
            {auth.user.picture && (
              <img className="topbar__avatar" src={auth.user.picture} alt="" referrerPolicy="no-referrer" />
            )}
            <span className="topbar__user-email">{auth.user.email}</span>
            <button type="button" className="topbar__logout" onClick={authLogout}>Sign out</button>
          </div>
        )}
      </header>

      <div className="app__body">
        <NavSidebar activeView={view === 'piece-detail' ? 'library' : (view === 'volume-detail' || view === 'chapter-detail') ? 'ebooks' : view} onNavigate={navigate} attentionManaged={attentionManaged} />

        {view === 'library' && (
          <Library
            pieces={pieces}
            brand={brand}
            onSelectPiece={openPiece}
            onNewPiece={() => setNewPieceOpen(true)}
            onSuggestTopics={() => { setTopicsOpen(true); if (topicSuggestions.length === 0) loadTopicSuggestions(); }}
            onCreateSocial={openInSocialStudio}
          />
        )}
        {view === 'ebooks' && (
          <EbooksView
            volumes={volumes}
            onSelectVolume={openVolume}
            onNewVolume={() => setNewVolumeOpen(true)}
          />
        )}
        {view === 'piece-detail' && selectedPiece && (
          <PieceDetail brand={brand} piece={selectedPiece} engineMode={engineMode} onBack={() => navigate('library')} onAction={handleAction} onToggleEngine={() => setEngineMode(p => !p)} />
        )}
        {view === 'volume-detail' && selectedVolume && (
          <VolumeDetail brand={brand} volume={selectedVolume} onBack={() => navigate('ebooks')} onAction={handleAction} onSelectChapter={(chSlug) => openChapter(selectedVolume.slug, chSlug)} />
        )}
        {view === 'chapter-detail' && chapterContext && (
          <PieceDetail
            brand={brand}
            piece={{ slug: chapterContext.chapterSlug, lifecycleState: 'review', stages: {}, renditions: {}, publications: {} }}
            engineMode={engineMode}
            onBack={() => openVolume(chapterContext.volumeSlug)}
            // Chapters aren't standalone pieces — they have no pieces/{slug}
            // endpoint. Route chapter-level actions to the volume so they hit
            // the real volume pipeline instead of a phantom piece slug.
            onAction={(action: string) => {
              if (action === 'pipeline') {
                handleAction('pipeline', chapterContext.volumeSlug);
                addToast('running', `Regenerating "${chapterContext.volumeSlug}" — all chapters.`);
              } else if (action.startsWith('render:')) {
                addToast('error', 'Render the ebook from the volume page → "Generate Ebook" (chapters render as one book).');
              } else {
                handleAction(action, chapterContext.volumeSlug);
              }
            }}
            onToggleEngine={() => setEngineMode(p => !p)}
            apiBase={`/api/${brand}/volumes/${chapterContext.volumeSlug}/chapters/${chapterContext.chapterSlug}`}
          />
        )}
        {view === 'quality' && (
          <QualityDashboard brand={brand} pieces={pieces} onSelectPiece={openPiece} />
        )}
        {view === 'cost' && (
          <CostView brand={brand} onSelectPiece={openPiece} onSelectVolume={openVolume} />
        )}
        {view === 'brand-performance' && (
          <div style={{ flex: 1, padding: '0 32px', overflowY: 'auto' }}>
            <BrandPerformanceTab brand={brand} />
          </div>
        )}
        {view === 'attention' && <AttentionView brand={brand} />}
        {view === 'social-studio' && (
          <SocialStudioView
            brand={brand}
            pieceSlug={selectedSlug}
            pieces={pieces.map((p) => ({ slug: p.slug, title: p.title }))}
            onPickPiece={() => navigate('library')}
          />
        )}
        {view === 'linkedin' && <LinkedInView brand={brand} />}
        {view === 'outreach' && <OutreachView brand={brand} />}
        {view === 'newsletter' && <NewsletterView brand={brand} addToast={addToast} replaceToast={replaceToast} />}
        {view === 'channels' && <ChannelsView brand={brand} />}
        {view === 'settings' && <SettingsView brand={brand} />}
        {view === 'activity' && <ActivityView brand={brand} pieces={pieces} />}
        {view === 'comments-approval' && <CommentsApprovalView brand={brand} onOpenAttention={() => navigate('attention')} />}
      </div>

      {cmdOpen && (
        <CommandPalette
          open={cmdOpen} brand={brand} brands={brands}
          pieces={pieces.map((p) => ({ slug: p.slug, lifecycleState: p.lifecycleState }))}
          volumes={volumes.map((v) => ({ slug: v.slug, title: v.title }))}
          onClose={() => setCmdOpen(false)}
          onSelectNode={(slug) => { openPiece(slug); setCmdOpen(false); }}
          onAction={handleAction}
          onSwitchBrand={(b) => { setBrand(b); setCmdOpen(false); }}
          onNavigateView={(v) => { setView(v); setSelectedSlug(null); setCmdOpen(false); }}
          attentionManaged={attentionManaged}
        />
      )}

      <Toasts toasts={toasts} onDismiss={dismissToast} />

      {previewModal && (
        <ChannelPreviewModal
          channel={previewModal.channel}
          data={previewModal.data}
          saving={previewSaving}
          onClose={() => { if (!previewSaving) setPreviewModal(null); }}
          onSaveDraft={() => {
            const { channel, slug } = previewModal;
            setPreviewSaving(true);
            const toastId = addToast('running', `Saving draft on ${channel}...`);
            fetch(`/api/${brand}/pieces/${slug}/publish`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ channel }),
            })
              .then(async (r) => {
                const d = await r.json();
                if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
                return d;
              })
              .then((d) => {
                replaceToast(toastId, 'success', `Draft saved on ${channel}`, d.permalink);
                setPieces((ps) => ps.map((p) => p.slug === slug
                  ? { ...p, publications: { ...p.publications, [channel]: { permalink: d.permalink, publishedAt: new Date().toISOString() } } }
                  : p));
                setPreviewModal(null);
              })
              .catch((err) => replaceToast(toastId, 'error', `Save draft failed: ${channel}`, err.message))
              .finally(() => setPreviewSaving(false));
          }}
        />
      )}

      {topicsOpen && (
        <Modal open onClose={() => setTopicsOpen(false)} title="Topic suggestions" hideHeader maxWidth={720}>
          <div style={{ padding: 20 }}>
            <h3 className="modal__title">{'\u{1F4A1}'} Topic suggestions</h3>
            <p className="modal__desc">
              Engine analyzes your corpus, identifies gaps, and generates topic suggestions.
              Click <strong>Generate prompt</strong> to get suggestions instantly.
            </p>

            {/* #181: suggestions now persist across "Use this topic" / reopen
                (the open handler only fetches when the list is empty). Explicit
                refresh stays available via the footer "Refresh" (reload) and
                "Generate fresh batch" (regenerate) buttons. */}

            {topicMessage && (
              <div style={{ background: '#FFF7E6', border: '1px solid #F58549', padding: '8px 12px', marginBottom: 16, fontSize: 13 }}>
                {topicMessage}
              </div>
            )}

            {topicsLoading && <p style={{ color: '#5B6678' }}>Loading…</p>}

            {!topicsLoading && topicSuggestions.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: '#5B6678' }}>
                No suggestions yet. Click <strong>Generate prompt</strong> to generate topic suggestions instantly.
              </div>
            )}

            {topicSuggestions.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                {(() => {
                  // Title-keyed map so render lookup survives any future
                  // filtering / reordering of the suggestions array.
                  const evalByTitle = new Map(
                    (topicEvaluated ?? []).map((e) => [e.suggestion.title, e.report]),
                  );
                  return topicSuggestions.map((s) => {
                  const evalReport = evalByTitle.get(s.title);
                  const applicableAxes = evalReport
                    ? Object.entries(evalReport.axes).filter(([, ax]) => ax.applies)
                    : [];
                  return (
                    <div key={s.title} style={{ border: '1px solid #D9D3BF', padding: 14, background: '#FAF7EC', position: 'relative' }}>
                      <button
                        onClick={() => dismissSuggestion(s.title)}
                        title="Dismiss — never suggest again"
                        style={{
                          position: 'absolute', top: 8, right: 8,
                          width: 22, height: 22,
                          background: 'transparent', border: '1px solid #C9C4B5',
                          cursor: 'pointer', fontSize: 12, color: '#7B8AA0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >{'✕'}</button>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6, paddingRight: 30 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: '#0F1828', flex: 1 }}>{s.title}</div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexShrink: 0 }}>
                          <div title="LLM self-assigned score" style={{ background: '#0E1525', color: '#F2EAD8', padding: '2px 8px', fontSize: 11, fontFamily: 'monospace' }}>
                            {s.score}/100
                          </div>
                          {evalReport && (
                            <div title="Deterministic eval composite (0-1, weighted across applicable axes)" style={{ background: '#3A5A40', color: '#F2EAD8', padding: '2px 8px', fontSize: 11, fontFamily: 'monospace' }}>
                              eval {Math.round(evalReport.composite * 100)}
                            </div>
                          )}
                        </div>
                      </div>
                      {s.scoreReason && (
                        <div style={{ fontSize: 12, color: '#5B6678', fontStyle: 'italic', marginBottom: 6, lineHeight: 1.4, paddingRight: 30 }}>
                          {s.scoreReason}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#5B6678', marginBottom: 6, fontFamily: 'monospace', letterSpacing: 1 }}>
                        {s.cluster.toUpperCase()} · {s.kind.toUpperCase()}
                        {typeof s.effortHours === 'number' && (
                          <span title="Estimated time-to-narrative in hours" style={{ marginLeft: 8 }}>· EFFORT {s.effortHours}H</span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: '#3A4458', marginBottom: 10, lineHeight: 1.4 }}>
                        {s.rationale}
                      </div>
                      {applicableAxes.length > 0 && (
                        <details style={{ marginBottom: 10, fontSize: 11, color: '#5B6678' }}>
                          <summary style={{ cursor: 'pointer', userSelect: 'none', fontFamily: 'monospace', letterSpacing: 0.5 }}>
                            EVAL BREAKDOWN — {applicableAxes.length} applicable axes
                          </summary>
                          <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
                            {applicableAxes
                              .sort(([, a], [, b]) => b.score - a.score)
                              .map(([name, ax]) => (
                                <div key={name} style={{ background: '#FFF', border: '1px solid #E5DEC8', padding: 6, fontSize: 11 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: 'monospace' }}>
                                    <span style={{ color: '#3A4458' }}>{name}</span>
                                    <span style={{ color: ax.score >= 0.7 ? '#3A5A40' : ax.score >= 0.4 ? '#7B6E2E' : 'var(--danger)' }}>
                                      {Math.round(ax.score * 100)}
                                    </span>
                                  </div>
                                  <div style={{ marginTop: 2, fontSize: 10, color: '#7B8AA0', lineHeight: 1.3 }}>
                                    {ax.reason}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </details>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary btn--sm" onClick={() => useTopicSuggestion(s)}>
                          Use this topic
                        </button>
                        <span style={{ fontSize: 11, color: '#7B8AA0', alignSelf: 'center', fontFamily: 'monospace' }}>
                          slug: {s.proposedSlug}
                        </span>
                      </div>
                    </div>
                  );
                  });
                })()}
              </div>
            )}

            <div className="modal__actions">
              <button className="btn btn-secondary" onClick={() => setTopicsOpen(false)}>Close</button>
              <button className="btn btn-secondary" onClick={loadTopicSuggestions} disabled={topicsLoading}>Refresh</button>
              {topicSuggestions.length > 0 ? (
                <button className="btn btn-primary" onClick={() => refreshTopicPrompt(true)} disabled={topicsLoading}>
                  Generate fresh batch
                </button>
              ) : (
                <button className="btn btn-primary" onClick={() => refreshTopicPrompt(false)} disabled={topicsLoading}>
                  Generate prompt
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {newPieceOpen && (
        <Modal open onClose={() => setNewPieceOpen(false)} title="New piece" hideHeader maxWidth={520}>
          <div style={{ padding: 20 }}>
            <h3 className="modal__title">New piece</h3>
            <p className="modal__desc">Give it a topic and the engine will research, write, format, and quality-check it.</p>
            <input
              className="modal__input"
              type="text"
              placeholder="e.g. Platform Engineering Best Practices"
              value={newPieceTopic}
              onChange={(e) => setNewPieceTopic(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNewPiece()}
              autoFocus
            />
            <div className="modal__actions">
              <button className="btn btn-secondary" onClick={() => setNewPieceOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleNewPiece} disabled={!newPieceTopic.trim()}>Create</button>
            </div>
          </div>
        </Modal>
      )}

      {newVolumeOpen && (
        <EbookWizard
          brand={brand}
          onClose={() => setNewVolumeOpen(false)}
          onDone={handleVolumeCreated}
        />
      )}
    </div>
  );
}
